/**
 * PourSetupLegacy Page — frozen reference copy of the pre-wizard edit-pour page.
 *
 * Route:
 *   /pours/:id/edit-legacy   → always edit mode
 *
 * This is a verbatim copy of PourSetup.tsx as it existed before it was
 * rebuilt into a 4-step wizard (matching PourCreate.tsx). Kept side-by-side
 * so the two layouts can be compared live. Not linked to from anywhere in
 * the app except the new wizard's "Compare with previous editor" link —
 * safe to delete once the comparison is no longer needed.
 *
 * Layout: a single scrollable page with a sticky section nav. Pour-level
 * configuration (functions, details, target/limit groups) is staged locally
 * and committed with one unified Save; the collection sections (locations,
 * contacts, attachments and the target/limit rows) manage their own rows via
 * dialogs that apply immediately.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  LockOutlined as LockOutlinedIcon,
  Dashboard as DashboardIcon,
  StopCircle as StopCircleIcon,
  Checklist as ChecklistIcon,
  Description as DescriptionIcon,
  Router as RouterIcon,
  Tune as TuneIcon,
  NotificationsNone as NotificationsIcon,
  AttachFile as AttachFileIcon,
  CheckCircle as CheckCircleIcon,
  CircleOutlined as CircleOutlinedIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  poursAPI,
  type PourDetail,
  type PourUpdate,
  type PourLookups,
} from '../services/pours';
import productsService, {
  type Product,
  type ProductCalibrationLink,
  type TargetGroup,
} from '../services/products';
import PourLocationsSection from '../components/pour/PourLocationsSection';
import PourContactsSection from '../components/pour/PourContactsSection';
import PourAttachmentsSection from '../components/pour/PourAttachmentsSection';
import PourTargetsSection from '../components/pour/PourTargetsSection';
import PourLimitsSection from '../components/pour/PourLimitsSection';
import PourFunctionSelector, {
  monitoringFunctionsToString,
  stringToMonitoringFunctions,
  deriveFunctionalityCode,
  type MonitoringFunctionId,
} from '../components/pour/PourFunctionSelector';
import { utcToLocalInput, localInputToUtc, formatInTimeZone } from '../utils/datetime';
import { useCompanyStrengthUnits } from '../utils/useCompanyStrengthUnit';
import { valueWithUnit } from '../utils/maturityDisplay';
import { apiErrorText } from '../utils/apiError';
import {
  STOP_AFTER_DAYS,
  STOP_AFTER_FINAL_TARGETS,
  STOP_AT_SET_TIME,
  STOP_TYPE_LABELS,
  isKnownStopType,
  stopTypeLabel,
} from '../utils/stopMonitoring';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PageMode = 'edit' | 'view';

interface FormData {
  /** Server-generated identifier (HM-yyMM-####). Read-only on edit/view. */
  PourCode: string;
  /** Human-readable pour status from VwPourInfo: 'Pending' | 'In Progress'
   *  | 'Complete' | 'Stop'. Drives Stop-button visibility. Empty on create. */
  PourStatus: string;
  PourName: string;
  Functionality: string;
  ProjectID: string;
  Location: string;
  ProjectRef: string;
  PourVolume: string;
  Levels: string;
  ProductID: string;
  TargetGroupID: string;
  LimitGroupID: string;
  EvaLimitGroupID: string;
  WeatherStationID: string;
  CalibrationID: string;
  MaturityMethod: string;
  ActivationEnergy: string;
  StopType: string;
  XHours: string;
  PreSetStopTime: string;
  Started: string;
  Finished: string;
}

const INITIAL_FORM_DATA: FormData = {
  PourCode: '',
  PourStatus: '',
  PourName: '',
  Functionality: '1',
  ProjectID: '',
  Location: '',
  ProjectRef: '',
  PourVolume: '',
  Levels: '',
  ProductID: '',
  TargetGroupID: '',
  LimitGroupID: '',
  EvaLimitGroupID: '',
  WeatherStationID: '',
  CalibrationID: '',
  MaturityMethod: '',
  ActivationEnergy: '',
  StopType: '',
  XHours: '',
  PreSetStopTime: '',
  Started: '',
  Finished: '',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map a pour status to a Chip colour (case-insensitive — the API returns
 *  'In Progress' with a capital P, which the old check missed). */
const statusChipColor = (status?: string): 'success' | 'warning' | 'default' => {
  switch ((status ?? '').toLowerCase()) {
    case 'complete':
      return 'success';
    case 'in progress':
      return 'warning';
    default:
      return 'default';
  }
};

/** Display label for a pour status — DB stores 'Complete', UI shows 'Completed'. */
const statusChipLabel = (status: string): string =>
  status.toLowerCase() === 'complete' ? 'Completed' : status;

const lookupName = (
  items: { id: string; name: string }[] | undefined,
  id: string,
): string => {
  if (!id) return '-';
  if (!items) return id;
  return items.find((item) => item.id === id)?.name ?? id;
};

/** Serialize the editable subset so we can detect unsaved changes. */
const snapshot = (form: FormData, fns: MonitoringFunctionId[]): string =>
  JSON.stringify({ form, fns: [...fns].sort() });

// ---------------------------------------------------------------------------
// Reusable display field (read-only)
// ---------------------------------------------------------------------------

const DisplayField: React.FC<{
  label: string;
  value: string | number | null | undefined;
}> = ({ label, value }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', mb: 0.25, textTransform: 'uppercase', letterSpacing: '0.04em' }}
    >
      {label}
    </Typography>
    <Typography variant="body1" sx={{ minHeight: 24 }}>
      {value === null || value === undefined || value === '' ? '-' : value}
    </Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// Summary metric tile
// ---------------------------------------------------------------------------

const MetricCard: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, px: 1.5, py: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.25, minHeight: 24 }}>
      {value === null || value === undefined || value === '' ? '-' : value}
    </Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// Section card wrapper
// ---------------------------------------------------------------------------

const Section: React.FC<{
  id: string;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ id, title, subtitle, icon, action, children }) => (
  <Paper id={id} variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 1.5 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        mb: subtitle ? 0.5 : 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        {icon && (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            }}
          >
            {icon}
          </Box>
        )}
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Paper>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PourSetupLegacy: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: pourId } = useParams<{ id: string }>();
  // This frozen reference page only ever renders the edit experience — it's
  // reached solely via /pours/:id/edit-legacy, not the URL-suffix detection
  // the live page uses.
  const mode: PageMode = 'edit';

  // ---- State ----
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [monitoringFunctions, setMonitoringFunctions] = useState<MonitoringFunctionId[]>([]);
  const [pourDetail, setPourDetail] = useState<PourDetail | null>(null);

  // Locked pours are read-only regardless of route mode — admins unlock
  // them from the Control Panel quick-view panel (UI Development doc 27.07).
  // This frozen reference page is always 'edit', so the only thing that can
  // force it read-only is a locked pour.
  const pourLocked = Boolean(pourDetail?.Locked);
  const isView = pourLocked;
  const [lookups, setLookups] = useState<PourLookups | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Baseline used for unsaved-changes detection + Discard.
  const [baselineKey, setBaselineKey] = useState('');
  const baselineData = useRef<{ form: FormData; funcs: MonitoringFunctionId[] }>({
    form: INITIAL_FORM_DATA,
    funcs: [],
  });

  // Stop-pour confirmation dialog + in-flight flag.
  const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
  const [stopping, setStopping] = useState(false);
  // null = unknown/loading; number = count of tank-bound locations on this
  // pour. Drives whether the dialog warns about tank reset.
  const [stopTankCount, setStopTankCount] = useState<number | null>(null);

  // Product info for the Product / recipe panel
  const [productDetail, setProductDetail] = useState<Product | null>(null);
  const [targetGroups, setTargetGroups] = useState<TargetGroup[]>([]);
  const [productCalibrations, setProductCalibrations] = useState<ProductCalibrationLink[]>([]);

  // Strengths are in the company's unit (StrengthFormat): the mix design's
  // company for its grade, the pour's for its targets. No unit is shown
  // until it is known.
  const [productStrengthUnit, pourStrengthUnit] = useCompanyStrengthUnits(
    productDetail?.company_id || pourDetail?.CompanyID,
    pourDetail?.CompanyID,
  );

  // Which section is currently shown (one at a time).
  const [activeSection, setActiveSection] = useState('functions');
  // Counts shown as nav badges (locations / contacts / attachments).
  const [counts, setCounts] = useState<{ locations?: number; contacts?: number; attachments?: number }>({});

  // ---- Keep Functionality (1/2/3) in sync with the multi-select cards ----
  useEffect(() => {
    if (monitoringFunctions.length === 0) return;
    const derived = deriveFunctionalityCode(monitoringFunctions);
    setFormData((prev) =>
      prev.Functionality === derived ? prev : { ...prev, Functionality: derived },
    );
  }, [monitoringFunctions]);

  const populateFormFromPour = useCallback((pour: PourDetail) => {
    const form: FormData = {
      PourCode: pour.PourCode ?? '',
      PourStatus: pour.PourStatus ?? '',
      PourName: pour.PourName ?? '',
      Functionality: pour.Functionality ?? '1',
      ProjectID: pour.ProjectID ?? '',
      Location: pour.Location ?? '',
      ProjectRef: pour.ProjectRef ?? '',
      PourVolume: pour.PourVolume != null ? String(pour.PourVolume) : '',
      Levels: pour.Levels ?? '',
      ProductID: pour.ProductID ?? '',
      TargetGroupID: pour.TargetGroupID ?? '',
      LimitGroupID: pour.LimitGroupID ?? '',
      EvaLimitGroupID: pour.EvaLimitGroupID ?? '',
      WeatherStationID: pour.WeatherStationID ?? '',
      CalibrationID: pour.CalibrationID ?? '',
      MaturityMethod: pour.MaturityMethod ?? '',
      ActivationEnergy: pour.ActivationEnergy != null ? String(pour.ActivationEnergy) : '',
      StopType: pour.StopType ?? '',
      XHours: pour.XHours != null ? String(pour.XHours) : '',
      PreSetStopTime: pour.PreSetStopTime ?? '',
      Started: pour.Started ?? '',
      Finished: pour.Finished ?? '',
    };
    const funcs = stringToMonitoringFunctions(pour.MonitoringFunctions);
    setFormData(form);
    setMonitoringFunctions(funcs);
    // Reset the dirty baseline to what the server just gave us.
    baselineData.current = { form, funcs };
    setBaselineKey(snapshot(form, funcs));

    if (pour.ProductID) {
      const productId = pour.ProductID;
      productsService
        .getProduct(productId)
        .then((prod) => {
          setProductDetail(prod);
          return Promise.all([
            productsService.listTargetGroups(productId),
            productsService.listProductCalibrations(productId),
          ]);
        })
        .then(([groups, calibrations]) => {
          setTargetGroups(groups);
          setProductCalibrations(calibrations);
        })
        .catch(() => { /* product may have been deleted */ });
    }
  }, []);

  // ---- Load lookups & pour ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [lookupsData, pour] = await Promise.all([
          poursAPI.getLookups(pourId),
          pourId ? poursAPI.getPourDetail(pourId) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setLookups(lookupsData);
        if (pour) {
          setPourDetail(pour);
          populateFormFromPour(pour);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(apiErrorText(err, 'Failed to load data'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pourId, populateFormFromPour]);

  // ---- Nav badge counts ----
  // The collection sections save immediately and only mount when visited, so
  // counts are fetched here and refreshed whenever the user switches sections.
  const refreshCounts = useCallback(async () => {
    if (!pourId) return;
    const [locations, contacts, attachments] = await Promise.all([
      poursAPI.getPourLocations2(pourId).then((r) => r.length).catch(() => undefined),
      poursAPI.getPourContacts(pourId).then((r) => r.length).catch(() => undefined),
      poursAPI.listPourAttachments(pourId).then((r) => r.length).catch(() => undefined),
    ]);
    setCounts({ locations, contacts, attachments });
  }, [pourId]);

  useEffect(() => {
    if (!loading) refreshCounts();
  }, [loading, activeSection, refreshCounts]);

  // ---- Dirty tracking ----
  const isDirty = !isView && baselineKey !== '' && snapshot(formData, monitoringFunctions) !== baselineKey;

  // Warn on full-page unload while there are unsaved changes.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Confirm before navigating away with unsaved changes.
  const guardedNavigate = useCallback(
    (to: string, options?: { replace?: boolean }) => {
      if (isDirty && !window.confirm(t('pourSetup.unsavedLeaveConfirm', 'You have unsaved changes. Leave without saving?'))) {
        return;
      }
      navigate(to, options);
    },
    [isDirty, navigate, t],
  );

  const handleDiscard = useCallback(() => {
    if (!window.confirm(t('pourSetup.discardConfirm', 'Discard your unsaved changes?'))) return;
    setFormData(baselineData.current.form);
    setMonitoringFunctions(baselineData.current.funcs);
  }, [t]);

  // ---- Change handlers ----
  const handleChange = (field: keyof FormData) => (e: any) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSelectChange = (field: keyof FormData) => (e: any) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  // Resolve the selected project's IANA timezone so datetime-local inputs
  // read/write in the project's local TZ instead of the browser's.
  const projectTimezoneIana = useMemo(
    () => lookups?.projects.find((p) => p.id === formData.ProjectID)?.timezone_iana,
    [lookups, formData.ProjectID],
  );

  const handleDateTimeChange =
    (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const utc = localInputToUtc(e.target.value, projectTimezoneIana);
      setFormData((prev) => ({ ...prev, [field]: utc }));
    };

  const fmtDateTime = (value: string | null | undefined): string =>
    value ? formatInTimeZone(value, projectTimezoneIana) : '-';

  // ---- Stop pour ----
  const openStopDialog = useCallback(async () => {
    if (!pourId) return;
    setStopConfirmOpen(true);
    setStopTankCount(null);
    try {
      const locations = await poursAPI.getPourLocations2(pourId);
      const count = locations.filter(
        (loc) => loc.TankID != null && loc.TankID !== ''
      ).length;
      setStopTankCount(count);
    } catch {
      setStopTankCount(0);
    }
  }, [pourId]);

  const handleStopConfirm = async () => {
    if (!pourId) return;
    setStopping(true);
    setError(null);
    try {
      const result = await poursAPI.stopPour(pourId);
      setStopConfirmOpen(false);
      setStopTankCount(null);
      if (result.already_stopped) {
        setSuccess(t('pourSetup.alreadyStopped', 'Pour was already stopped'));
      } else {
        const tankNote = result.tanks_notified
          ? t(
              'pourSetup.stopSuccessWithTanks',
              { defaultValue: 'Pour stopped. {{count}} tank(s) reset.', count: result.tanks_notified },
            )
          : t('pourSetup.stopSuccess', 'Pour stopped.');
        setSuccess(String(tankNote));
      }
      const refreshed = await poursAPI.getPourDetail(pourId);
      setPourDetail(refreshed);
      populateFormFromPour(refreshed);
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to stop pour'));
    } finally {
      setStopping(false);
    }
  };

  // ---- Submit (unified save of pour-level fields) ----
  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!pourId || mode !== 'edit') return;
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      // Maturity inputs (method, activation energy, datum, specified
      // temperature) are never sent: the server copies them from the mix
      // design's maturity group, as on the main edit page.
      const payload: PourUpdate = {
        PourName: formData.PourName.trim(),
        Functionality: formData.Functionality || undefined,
        MonitoringFunctions: monitoringFunctions.length > 0
          ? monitoringFunctionsToString(monitoringFunctions)
          : undefined,
        ProjectID: formData.ProjectID || undefined,
        ProductID: formData.ProductID || undefined,
        StopType: formData.StopType || undefined,
        Location: formData.Location || undefined,
        ProjectRef: formData.ProjectRef || undefined,
        PourVolume: formData.PourVolume ? Number(formData.PourVolume) : undefined,
        Levels: formData.Levels || undefined,
        TargetGroupID: formData.TargetGroupID || undefined,
        LimitGroupID: formData.LimitGroupID || undefined,
        EvaLimitGroupID: formData.EvaLimitGroupID || undefined,
        CalibrationID: formData.CalibrationID || undefined,
        // No WeatherStationID: the server takes it from the pour's weather
        // station location, so a save never changes it.
        XHours: formData.XHours ? Number(formData.XHours) : undefined,
        PreSetStopTime: formData.PreSetStopTime || undefined,
        Started: formData.Started || undefined,
        Finished: formData.Finished || undefined,
      };
      const updated = await poursAPI.updatePour(pourId, payload);
      setPourDetail(updated);
      // Saved values become the new baseline → clears the dirty state.
      baselineData.current = { form: formData, funcs: monitoringFunctions };
      setBaselineKey(snapshot(formData, monitoringFunctions));
      setSuccess(t('pourSetup.savedSuccess', 'Pour saved successfully'));
    } catch (err: any) {
      // The server's words, as text: a refused save (e.g. a pour that stops
      // after its final targets losing its maturity or targets) says why.
      setError(apiErrorText(err, 'Failed to save pour'));
    } finally {
      setSaving(false);
    }
  };

  // ---- Conditional visibility derived from the monitoring functions ----
  const fnSet = useMemo(() => new Set(monitoringFunctions), [monitoringFunctions]);
  const hasTemp = fnSet.has('Temperature Only');
  const hasMaturity = fnSet.has('Concrete Maturity');
  const hasTMC = fnSet.has('Temperature Match Curing');
  const hasEvap = fnSet.has('Calculate Concrete Evaporation Rate');
  const hasWeather = fnSet.has('Onsite Weather Conditions');

  const showProduct = hasTemp || hasMaturity || hasTMC || hasEvap;
  const showMaturityTarget = hasMaturity || hasTMC;
  const showMassLimit = hasTemp;
  const showEvapLimit = hasEvap;
  const needsWeather = hasWeather || hasEvap;
  const showTargetsLimits = showMaturityTarget || showMassLimit || showEvapLimit || needsWeather;

  // Only one section is shown at a time. If the active section becomes hidden
  // (e.g. Targets & limits no longer applies after a function change), fall
  // back to Functions so the content pane is never blank.
  useEffect(() => {
    if (activeSection === 'limits' && !showTargetsLimits) {
      setActiveSection('functions');
    }
  }, [activeSection, showTargetsLimits]);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    // Bring the content pane back into view when switching from far down.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ---- Loading / not-found ----
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!pourId || !pourDetail) {
    return (
      <Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Alert severity="warning">{t('pourSetup.notFound', 'Pour not found.')}</Alert>
      </Box>
    );
  }

  // ---- Derived display values ----
  const showXHoursField = formData.StopType === '7' || formData.StopType === '31';
  // PreSetStopTime is the stop time on 'Stop at a set time' and the
  // Evaporation Calculation Stop Time on any other stop, as on PourSetup.
  const stopsAtSetTime = formData.StopType === STOP_AT_SET_TIME;
  const showPresetField = stopsAtSetTime || hasEvap;
  const presetLabel = stopsAtSetTime
    ? t('pourSetup.stopTime', 'Stop time')
    : t('pourSetup.evaporationCalcStopTime', 'Evaporation Calculation Stop Time');
  // Only the pour's own calibration: a pour without one has no strength, so
  // no other link of the mix design (active or not) is shown in its place.
  const selectedCalibration = formData.CalibrationID
    ? productCalibrations.find((c) => c.calibration_id === formData.CalibrationID)
    : undefined;
  const calibrationDisplay = !formData.CalibrationID
    ? t('pourSetup.noCalibration', 'None')
    : selectedCalibration
      ? [selectedCalibration.calibration_code, selectedCalibration.calibration_name]
          .filter(Boolean).join(' · ')
      : pourDetail.CalibrationName || undefined;
  // Maturity is calculated with the pour's own copy of the mix design
  // group's inputs (the server copies them, then freezes them at the first
  // reading; later group edits do not reach the pour), so the pour's saved
  // value comes first. The group's value shows only when the pour has none,
  // and the calibration's activation energy is never used.
  const activationEnergyDisplay =
    pourDetail.ActivationEnergy ?? productDetail?.maturity_activation_energy;

  const projectName = lookupName(lookups?.projects, formData.ProjectID);

  // ---- Per-section nav status ----
  const functionsComplete = monitoringFunctions.length > 0;
  const detailsComplete =
    !!formData.PourName.trim() && !!formData.ProjectID && (!showProduct || !!formData.ProductID);
  const limitsConfigured =
    !!formData.TargetGroupID || !!formData.LimitGroupID || !!formData.EvaLimitGroupID || !!formData.WeatherStationID;

  const doneBadge = () => (
    <Tooltip title={t('pourSetup.sectionComplete', 'Set up')}>
      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
    </Tooltip>
  );
  const warnBadge = () => (
    <Tooltip title={t('pourSetup.sectionNeedsInfo', 'Needs info')}>
      <ErrorOutlineIcon sx={{ fontSize: 16, color: 'warning.main' }} />
    </Tooltip>
  );
  const countBadge = (n?: number) =>
    n != null ? (
      <Typography variant="caption" color="text.secondary">{n}</Typography>
    ) : null;

  const statusFor = (complete: boolean): React.ReactNode =>
    isView ? null : complete ? doneBadge() : warnBadge();

  const navItems: { id: string; label: string; icon: React.ReactNode; trailing: React.ReactNode }[] = [
    { id: 'functions', label: t('pourSetup.secFunctions', 'Functions'), icon: <ChecklistIcon fontSize="small" />, trailing: statusFor(functionsComplete) },
    { id: 'details', label: t('pourSetup.secDetails', 'Pour details'), icon: <DescriptionIcon fontSize="small" />, trailing: statusFor(detailsComplete) },
    { id: 'hardware', label: t('pourSetup.secHardware', 'Hardware'), icon: <RouterIcon fontSize="small" />, trailing: counts.locations === 0 && !isView ? warnBadge() : countBadge(counts.locations) },
    ...(showTargetsLimits
      ? [{ id: 'limits', label: t('pourSetup.secTargetsLimits', 'Targets & limits'), icon: <TuneIcon fontSize="small" />, trailing: isView ? null : (limitsConfigured ? doneBadge() : null) }]
      : []),
    { id: 'notifications', label: t('pourSetup.secNotifications', 'Notifications'), icon: <NotificationsIcon fontSize="small" />, trailing: countBadge(counts.contacts) },
    { id: 'attachments', label: t('pourSetup.secAttachments', 'Attachments'), icon: <AttachFileIcon fontSize="small" />, trailing: countBadge(counts.attachments) },
  ];

  const gridSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    gap: 2,
  } as const;

  return (
    <Box>
      {/* Top action bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => guardedNavigate('/pours')}
          size="small"
          sx={{ textTransform: 'none' }}
        >
          {t('pourSetup.backToPours', 'Back to Pours')}
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {pourId && (
            <Button
              variant="outlined"
              size="medium"
              startIcon={<DashboardIcon />}
              onClick={() => guardedNavigate(`/pours/${pourId}/dashboard`)}
              disabled={saving || stopping}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', px: 3, py: 1 }}
            >
              {t('pourSetup.dashboard', 'Dashboard')}
            </Button>
          )}

          {pourId &&
            (formData.PourStatus === 'In Progress' ||
              formData.PourStatus === 'Pending') && (
              <Button
                variant="outlined"
                color="warning"
                size="medium"
                startIcon={<StopCircleIcon />}
                onClick={openStopDialog}
                disabled={saving || stopping}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap', px: 3, py: 1 }}
              >
                {t('pourSetup.stop', 'Stop')}
              </Button>
            )}

          {isView ? (
            !pourLocked && (
              <Button
                variant="contained"
                size="medium"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/pours/${pourId}/edit`, { replace: true })}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap', px: 3, py: 1 }}
              >
                {t('pourSetup.edit', 'Edit')}
              </Button>
            )
          ) : (
            <Button
              variant="outlined"
              size="medium"
              startIcon={<VisibilityIcon />}
              onClick={() => guardedNavigate(`/pours/${pourId}`, { replace: true })}
              disabled={saving || stopping}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', px: 3, py: 1 }}
            >
              {t('pourSetup.view', 'View')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Locked banner — the pour is read-only until an admin unlocks it */}
      {pourLocked && (
        <Alert severity="info" icon={<LockOutlinedIcon fontSize="small" />} sx={{ mb: 2 }}>
          {t(
            'pourSetup.lockedBanner',
            'This pour is locked and read-only. An administrator can unlock it from the Control Panel to allow changes.',
          )}
        </Alert>
      )}

      {/* Pour summary card */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 0.25 }}>
              {pourDetail.PourName || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pourDetail.PourCode || '-'}
              {projectName !== '-' ? ` · ${projectName}` : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {pourDetail.PourStatus && (
              <Chip
                label={statusChipLabel(pourDetail.PourStatus)}
                size="small"
                color={statusChipColor(pourDetail.PourStatus)}
              />
            )}
            {pourLocked && (
              <Chip
                label={t('pourSetup.locked', 'Locked')}
                size="small"
                icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
              />
            )}
            <Chip
              label={isView ? t('pourSetup.viewing', 'Viewing') : t('pourSetup.editing', 'Editing')}
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
            gap: 2,
          }}
        >
          <MetricCard label={t('pourSetup.product', 'Product')} value={productDetail?.code} />
          <MetricCard label={t('pourSetup.started', 'Started')} value={fmtDateTime(formData.Started)} />
          <MetricCard label={t('pourSetup.setupBy', 'Setup By')} value={pourDetail.SetupBy} />
          <MetricCard label={t('pourSetup.timeZone', 'Time Zone')} value={pourDetail.TimeZone} />
        </Box>
      </Paper>

      {/* Sticky save bar (edit mode only) */}
      {!isView && (
        <Box
          sx={{
            position: 'sticky',
            top: 8,
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            px: 2,
            py: 1.25,
            mb: 2,
            borderRadius: 1.5,
            border: 1,
            borderColor: isDirty ? 'warning.light' : 'divider',
            backgroundColor: isDirty
              ? (theme) => alpha(theme.palette.warning.main, 0.08)
              : 'background.paper',
            boxShadow: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            {isDirty ? (
              <CircleOutlinedIcon fontSize="small" color="warning" />
            ) : (
              <CheckCircleIcon fontSize="small" color="success" />
            )}
            <Typography variant="body2">
              {isDirty
                ? t('pourSetup.unsavedChanges', 'Unsaved changes')
                : t('pourSetup.allChangesSaved', 'All changes saved')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={handleDiscard}
              disabled={!isDirty || saving}
              sx={{ textTransform: 'none' }}
            >
              {t('pourSetup.discard', 'Discard')}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={!isDirty || saving || !formData.PourName.trim()}
              onClick={() => handleSubmit()}
              sx={{ textTransform: 'none', px: 2 }}
            >
              {saving ? t('pourSetup.saving', 'Saving...') : t('pourSetup.saveChanges', 'Save changes')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Two-column layout: section nav + the one active section */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start', gap: 2.5 }}>
        {/* Section nav — vertical sidebar on desktop, horizontal scroll row on mobile */}
        <Box
          component="nav"
          sx={{
            position: { md: 'sticky' },
            top: { md: 72 },
            flex: { xs: '0 0 auto', md: '0 0 168px' },
            width: { xs: '100%', md: 'auto' },
            display: 'flex',
            flexDirection: { xs: 'row', md: 'column' },
            gap: 0.5,
            overflowX: { xs: 'auto', md: 'visible' },
            pb: { xs: 0.5, md: 0 },
          }}
        >
          {navItems.map((item) => {
            const active = activeSection === item.id;
            return (
              <Box
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.9,
                  borderRadius: 1,
                  cursor: 'pointer',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  borderLeft: { md: '2px solid' },
                  borderLeftColor: { md: active ? 'primary.main' : 'transparent' },
                  color: active ? 'primary.main' : 'text.primary',
                  backgroundColor: (theme) =>
                    active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  fontWeight: active ? 600 : 400,
                  '&:hover': {
                    backgroundColor: (theme) =>
                      active ? alpha(theme.palette.primary.main, 0.14) : 'action.hover',
                  },
                }}
              >
                <Box sx={{ display: 'flex', color: active ? 'primary.main' : 'text.secondary' }}>
                  {item.icon}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 'inherit', flex: 1 }}>
                  {item.label}
                </Typography>
                {item.trailing}
              </Box>
            );
          })}
        </Box>

        {/* Sections */}
        <Box
          component={isView ? 'div' : 'form'}
          onSubmit={isView ? undefined : handleSubmit}
          sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {/* ---- Monitoring functions ---- */}
          {activeSection === 'functions' && (
            <Section
              id="functions"
              icon={<ChecklistIcon fontSize="small" />}
              title={t('pourSetup.monitoringFunctions', 'Monitoring Functions')}
            >
              {isView ? (
                monitoringFunctions.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {monitoringFunctions.map((fn) => (
                      <Chip key={fn} label={fn} size="small" color="primary" variant="outlined" />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body1" color="text.secondary">-</Typography>
                )
              ) : (
                <PourFunctionSelector value={monitoringFunctions} onChange={setMonitoringFunctions} />
              )}
            </Section>
          )}

          {/* ---- Pour details ---- */}
          {activeSection === 'details' && (
          <Section
            id="details"
            icon={<DescriptionIcon fontSize="small" />}
            title={t('pourSetup.secDetails', 'Pour details')}
          >
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.06em', mb: 1.5 }}>
              {t('pourSetup.groupIdentification', 'Identification')}
            </Typography>
            <Box sx={gridSx}>
              {isView ? (
                <DisplayField label={t('pourSetup.pourName', 'Pour Name')} value={formData.PourName} />
              ) : (
                <TextField
                  label={t('pourSetup.pourName', 'Pour Name')}
                  value={formData.PourName}
                  onChange={handleChange('PourName')}
                  required
                  fullWidth size="small"
                  inputProps={{ maxLength: 50 }}
                />
              )}

              {isView ? (
                <DisplayField
                  label={t('pourSetup.project', 'Project')}
                  value={lookupName(lookups?.projects, formData.ProjectID)}
                />
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel>{t('pourSetup.project', 'Project')}</InputLabel>
                  <Select
                    value={formData.ProjectID}
                    onChange={handleSelectChange('ProjectID')}
                    label={t('pourSetup.project', 'Project')}
                  >
                    <MenuItem value=""><em>{t('pourSetup.none', 'None')}</em></MenuItem>
                    {lookups?.projects.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {isView ? (
                <DisplayField label={t('pourSetup.location', 'Location')} value={formData.Location} />
              ) : (
                <TextField
                  label={t('pourSetup.location', 'Location')}
                  value={formData.Location}
                  onChange={handleChange('Location')}
                  fullWidth size="small"
                />
              )}

              {isView ? (
                <DisplayField label={t('pourSetup.projectRef', 'Drawing / reference no.')} value={formData.ProjectRef} />
              ) : (
                <TextField
                  label={t('pourSetup.projectRef', 'Drawing / reference no.')}
                  value={formData.ProjectRef}
                  onChange={handleChange('ProjectRef')}
                  fullWidth size="small"
                />
              )}

              {isView ? (
                <DisplayField
                  label={t('pourSetup.pourVolume', 'Pour volume')}
                  value={formData.PourVolume ? `${formData.PourVolume} m³` : undefined}
                />
              ) : (
                <TextField
                  label={t('pourSetup.pourVolume', 'Pour volume')}
                  value={formData.PourVolume}
                  onChange={handleChange('PourVolume')}
                  type="number"
                  fullWidth size="small"
                  InputProps={{ endAdornment: <InputAdornment position="end">m³</InputAdornment> }}
                />
              )}

            </Box>

            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.06em', mt: 3, mb: 1.5 }}>
              {t('pourSetup.groupTiming', 'Timing')}
            </Typography>
            <Box sx={gridSx}>
              {isView ? (
                <DisplayField label={t('pourSetup.pourStarted', 'Pour Started')} value={fmtDateTime(formData.Started)} />
              ) : (
                <TextField
                  label={t('pourSetup.pourStarted', 'Pour Started')}
                  value={utcToLocalInput(formData.Started, projectTimezoneIana)}
                  onChange={handleDateTimeChange('Started')}
                  type="datetime-local"
                  fullWidth size="small"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}

              {isView ? (
                <DisplayField label={t('pourSetup.pourFinished', 'Pour Finished')} value={fmtDateTime(formData.Finished)} />
              ) : (
                <TextField
                  label={t('pourSetup.pourFinished', 'Pour Finished')}
                  value={utcToLocalInput(formData.Finished, projectTimezoneIana)}
                  onChange={handleDateTimeChange('Finished')}
                  type="datetime-local"
                  fullWidth size="small"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}

              {/* Stop monitoring */}
              {isView ? (
                <DisplayField
                  label={t('pourSetup.stopRecordingWhen', 'Stop monitoring')}
                  value={stopTypeLabel(formData.StopType)}
                />
              ) : (
                // The same labels as the main edit page (utils/stopMonitoring).
                // A legacy code this app does not offer stays shown as it is.
                <FormControl fullWidth size="small">
                  <InputLabel>{t('pourSetup.stopRecordingWhen', 'Stop monitoring')}</InputLabel>
                  <Select
                    value={formData.StopType}
                    onChange={handleSelectChange('StopType')}
                    label={t('pourSetup.stopRecordingWhen', 'Stop monitoring')}
                  >
                    {formData.StopType === '' && (
                      <MenuItem value="" disabled><em>Not set</em></MenuItem>
                    )}
                    <MenuItem value={STOP_AFTER_DAYS}>{STOP_TYPE_LABELS[STOP_AFTER_DAYS]}</MenuItem>
                    <MenuItem value={STOP_AFTER_FINAL_TARGETS}>
                      {STOP_TYPE_LABELS[STOP_AFTER_FINAL_TARGETS]}
                    </MenuItem>
                    <MenuItem value={STOP_AT_SET_TIME}>{STOP_TYPE_LABELS[STOP_AT_SET_TIME]}</MenuItem>
                    {formData.StopType !== '' && !isKnownStopType(formData.StopType) && (
                      <MenuItem value={formData.StopType} disabled>
                        {stopTypeLabel(formData.StopType)}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}

              {showXHoursField &&
                (isView ? (
                  <DisplayField
                    label={formData.StopType === '31' ? t('pourSetup.days', 'Days') : t('pourSetup.hours', 'Hours')}
                    value={formData.XHours}
                  />
                ) : (
                  <TextField
                    label={formData.StopType === '31' ? t('pourSetup.days', 'Days') : t('pourSetup.hours', 'Hours')}
                    value={formData.XHours}
                    onChange={handleChange('XHours')}
                    type="number"
                    fullWidth size="small"
                  />
                ))}

              {showPresetField &&
                (isView ? (
                  <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                    <DisplayField label={presetLabel} value={fmtDateTime(formData.PreSetStopTime)} />
                  </Box>
                ) : (
                  <TextField
                    label={presetLabel}
                    value={utcToLocalInput(formData.PreSetStopTime, projectTimezoneIana)}
                    onChange={handleDateTimeChange('PreSetStopTime')}
                    type="datetime-local"
                    fullWidth size="small"
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ gridColumn: { sm: '1 / -1' } }}
                  />
                ))}

              {/* Additional notes */}
              {isView ? (
                <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                  <DisplayField label={t('pourSetup.levelsComments', 'Additional notes')} value={formData.Levels} />
                </Box>
              ) : (
                <TextField
                  label={t('pourSetup.levelsComments', 'Additional notes')}
                  value={formData.Levels}
                  onChange={handleChange('Levels')}
                  multiline rows={2}
                  fullWidth size="small"
                  sx={{ gridColumn: { sm: '1 / -1' } }}
                />
              )}
            </Box>

            {/* Product / recipe (read-only mix design) */}
            {showProduct && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ fontWeight: 600, mb: 1.5, textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.06em' }}
                >
                  {t('pourSetup.mixDesign', 'Product / recipe')}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
                    gap: 2,
                  }}
                >
                  <DisplayField label={t('pourSetup.productCode', 'Product Code')} value={productDetail?.code} />
                  <DisplayField label={t('pourSetup.description', 'Description')} value={productDetail?.description} />
                  <DisplayField
                    label={t('pourSetup.supplierPlant', 'Supplier / Batch Plant')}
                    value={pourDetail.SupplierPlant || productDetail?.supplier_plant_name}
                  />
                  <DisplayField
                    label={t('pourSetup.strengthGrade', 'Strength Grade')}
                    value={productDetail?.strength_grade != null ? valueWithUnit(productDetail.strength_grade, productStrengthUnit) : undefined}
                  />
                  {showMaturityTarget && (
                    <DisplayField label={t('pourSetup.maturityMethod', 'Maturity Method')} value={productDetail?.maturity_method} />
                  )}
                  {showMaturityTarget && (
                    <DisplayField label={t('pourSetup.activationEnergy', 'Activation Energy')} value={activationEnergyDisplay} />
                  )}
                  {showMaturityTarget && (
                    <DisplayField label={t('pourSetup.calibration', 'Calibration')} value={calibrationDisplay} />
                  )}
                </Box>
              </Box>
            )}
          </Section>
          )}

          {/* ---- Hardware & locations ---- */}
          {activeSection === 'hardware' && (
          <Section
            id="hardware"
            icon={<RouterIcon fontSize="small" />}
            title={t('pourSetup.monitoringLocations', 'Monitoring Locations')}
            subtitle={isView ? undefined : t('pourSetup.listChangesImmediate', 'Changes here are saved immediately.')}
          >
            {lookups && (
              <PourLocationsSection
                pourId={pourId}
                mode={mode}
                lookups={lookups}
                pourContext={{
                  WeatherStationID: formData.WeatherStationID,
                  TargetGroupID: formData.TargetGroupID,
                  CalibrationID: formData.CalibrationID,
                  MaturityMethod: formData.MaturityMethod,
                  SpecifiedTemp: pourDetail?.SpecifiedTemp,
                  Functionality: formData.Functionality,
                }}
                projectTimezoneIana={projectTimezoneIana}
                strengthUnit={pourStrengthUnit}
              />
            )}
          </Section>
          )}

          {/* ---- Targets & limits ---- */}
          {activeSection === 'limits' && showTargetsLimits && (
            <Section
              id="limits"
              icon={<TuneIcon fontSize="small" />}
              title={t('pourSetup.secTargetsLimits', 'Targets & limits')}
              subtitle={t('pourSetup.targetsLimitsIntro', 'Only the groups relevant to your selected functions are shown.')}
            >
              {/* Pour-level group selectors (staged with the unified Save) */}
              <Box sx={gridSx}>
                {showMaturityTarget &&
                  (isView ? (
                    <DisplayField
                      label={t('pourSetup.targetGroup', 'Maturity target group')}
                      value={
                        targetGroups.find((g) => g.target_group_id === formData.TargetGroupID)?.target_group_name
                      }
                    />
                  ) : (
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('pourSetup.targetGroup', 'Maturity target group')}</InputLabel>
                      <Select
                        value={formData.TargetGroupID}
                        onChange={handleSelectChange('TargetGroupID')}
                        label={t('pourSetup.targetGroup', 'Maturity target group')}
                      >
                        <MenuItem value=""><em>{t('pourSetup.none', 'None')}</em></MenuItem>
                        {targetGroups.map((g) => (
                          <MenuItem key={g.target_group_id} value={g.target_group_id}>
                            {g.target_group_name ?? g.target_group_id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ))}

                {showMassLimit &&
                  (isView ? (
                    <DisplayField
                      label={t('pourSetup.limitGroup', 'Mass concrete limit group')}
                      value={lookupName(lookups?.mass_concrete_defaults, formData.LimitGroupID)}
                    />
                  ) : (
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('pourSetup.limitGroup', 'Mass concrete limit group')}</InputLabel>
                      <Select
                        value={formData.LimitGroupID}
                        onChange={handleSelectChange('LimitGroupID')}
                        label={t('pourSetup.limitGroup', 'Mass concrete limit group')}
                      >
                        <MenuItem value=""><em>{t('pourSetup.none', 'None')}</em></MenuItem>
                        {lookups?.mass_concrete_defaults.map((item) => (
                          <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ))}

                {showEvapLimit &&
                  (isView ? (
                    <DisplayField
                      label={t('pourSetup.evaporationLimitGroup', 'Evaporation limit group')}
                      value={lookupName(lookups?.evaporation_defaults, formData.EvaLimitGroupID)}
                    />
                  ) : (
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('pourSetup.evaporationLimitGroup', 'Evaporation limit group')}</InputLabel>
                      <Select
                        value={formData.EvaLimitGroupID}
                        onChange={handleSelectChange('EvaLimitGroupID')}
                        label={t('pourSetup.evaporationLimitGroup', 'Evaporation limit group')}
                      >
                        <MenuItem value=""><em>{t('pourSetup.none', 'None')}</em></MenuItem>
                        {lookups?.evaporation_defaults.map((item) => (
                          <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ))}

                {/* Read only: the server links the pour's weather station
                    from its weather station location (Hardware / Sensors). */}
                {needsWeather && (
                  <DisplayField
                    label={t('pourSetup.weatherStation', 'Weather station')}
                    value={
                      formData.WeatherStationID || isView
                        ? lookupName(lookups?.weather_stations, formData.WeatherStationID)
                        : t('pourSetup.weatherStationFromHardware', 'Add a weather station on Hardware / Sensors')
                    }
                  />
                )}
              </Box>

              {/* Target / limit rows (saved immediately via their own dialogs) */}
              {showMaturityTarget && lookups && (
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1.5, textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.06em' }}
                  >
                    {t('pourSetup.targets', 'Targets')}
                  </Typography>
                  <PourTargetsSection
                    pourId={pourId}
                    mode={mode}
                    lookups={lookups}
                    calibrationId={pourDetail?.CalibrationID}
                    // The pour's stored method names the target unit (a blank
                    // method is calculated as EA); unknown until it loads.
                    maturityMethod={pourDetail ? formData.MaturityMethod : null}
                    // The Ts the engine calculates with (20 °C when null).
                    specifiedTemp={pourDetail?.SpecifiedTemp}
                    strengthUnit={pourStrengthUnit}
                  />
                </Box>
              )}

              {(showMassLimit || showEvapLimit) && lookups && (
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 1.5, textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.06em' }}
                  >
                    {t('pourSetup.limits', 'Limits')}
                  </Typography>
                  <PourLimitsSection pourId={pourId} mode={mode} lookups={lookups} />
                </Box>
              )}
            </Section>
          )}

          {/* ---- Notifications ---- */}
          {activeSection === 'notifications' && (
          <Section
            id="notifications"
            icon={<NotificationsIcon fontSize="small" />}
            title={t('pourSetup.contacts', 'Contacts')}
            subtitle={isView ? undefined : t('pourSetup.listChangesImmediate', 'Changes here are saved immediately.')}
          >
            {lookups && <PourContactsSection pourId={pourId} mode={mode} lookups={lookups} />}
          </Section>
          )}

          {/* ---- Attachments ---- */}
          {activeSection === 'attachments' && (
          <Section
            id="attachments"
            icon={<AttachFileIcon fontSize="small" />}
            title={t('pourSetup.attachments', 'Attachments')}
            subtitle={isView ? undefined : t('pourSetup.listChangesImmediate', 'Changes here are saved immediately.')}
          >
            <PourAttachmentsSection pourId={pourId} mode={mode} />
          </Section>
          )}
        </Box>
      </Box>

      {/* Stop-pour confirmation dialog */}
      <Dialog
        open={stopConfirmOpen}
        onClose={() => {
          if (!stopping) {
            setStopConfirmOpen(false);
            setStopTankCount(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={stopping}
      >
        <DialogTitle>{t('pourSetup.stopConfirmTitle', 'Stop Pour')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
            {t('pourSetup.stopConfirmBody', {
              code: formData.PourCode || formData.PourName || pourId,
            })}
          </DialogContentText>
          {stopTankCount !== null && stopTankCount > 0 && (
            <DialogContentText sx={{ mt: 2, fontWeight: 500 }}>
              {t('pourSetup.stopConfirmTankNote', { count: stopTankCount })}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStopConfirmOpen(false);
              setStopTankCount(null);
            }}
            disabled={stopping}
          >
            {t('pourSetup.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleStopConfirm}
            disabled={stopping}
            startIcon={
              stopping ? <CircularProgress size={20} color="inherit" /> : <StopCircleIcon />
            }
          >
            {stopping
              ? t('pourSetup.stopping', 'Stopping...')
              : t('pourSetup.confirmStop', 'Stop Pour')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PourSetupLegacy;
